'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { format } from 'date-fns';
import ExportMenuItem from './ExportMenuItem';
import { useNotification } from 'src/context/notification';
import useGetUserActivitiesExport from 'src/hooks/userActivities/useGetUserActivitiesExport';

interface DateRange {
  gte: string;
  lte: string;
}

interface CompanyTableExportMenuProps {
  isExporting?: boolean;
  disabled?: boolean;
  table?: string;
  initialDateRange?: DateRange;
  companyId: string;
}

const CompanyTableExportMenu: React.FC<CompanyTableExportMenuProps> = ({
  isExporting = false,
  disabled = false,
  table = 'companies',
  initialDateRange,
  companyId,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { setNotification } = useNotification();

  const {
    isLoading: isExportLoading,
    error: exportError,
    executeExport,
  } = useGetUserActivitiesExport();

  // Default date range: January 1, 2022 to current date
  const getDefaultDateRange = useCallback((): DateRange => {
    const today = new Date();
    return {
      gte: initialDateRange?.gte || '2022-01-01',
      lte: initialDateRange?.lte || format(today, 'yyyy-MM-dd'),
    };
  }, [initialDateRange]);

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

  // Date validation error state
  const [dateErrors, setDateErrors] = useState<{
    start?: string;
    end?: string;
  }>({});

  // Date constraints
  const MAX_DATE = format(new Date(), 'yyyy-MM-dd');

  // Handle click outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [handleClickOutside, isMenuOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    },
    [isMenuOpen]
  );

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleKeyDown, isMenuOpen]);

  const handleExport = useCallback(
    async (type: string) => {
      try {
        await executeExport(companyId, dateRange, type);

        // Show success notification
        setNotification({
          title: 'Success',
          message: 'CSV export completed successfully',
          type: 'success',
        });
      } catch (error) {
        // Show error notification
        setNotification({
          title: 'Error',
          message: 'Error exporting data, please try again.',
          type: 'error',
        });
      } finally {
        setIsMenuOpen(false);
      }
    },
    [executeExport, companyId, dateRange, setNotification]
  );

  // Show export error if present
  React.useEffect(() => {
    if (exportError) {
      setNotification({
        title: 'Error',
        message: exportError || 'Error exporting data, please try again.',
        type: 'error',
      });
    }
  }, [exportError, setNotification]);

  // Use combined loading state
  const combinedIsExporting = isExporting || isExportLoading;

  const toggleMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isMenuOpen) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    },
    [isMenuOpen]
  );

  /**
   * Handles start date changes with validation
   * Prevents selection of start dates later than the current end date
   */
  const handleStartDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newStartDate = event.target.value;
      if (!newStartDate) return;

      // Validate that start date is not later than end date
      if (newStartDate > dateRange.lte) {
        setDateErrors((prev) => ({
          ...prev,
          start: 'Start date cannot be later than end date',
        }));
        return; // Prevent the change
      }

      // Clear any existing start date error
      setDateErrors((prev) => ({
        ...prev,
        start: undefined,
      }));

      setDateRange((prev) => ({ ...prev, gte: newStartDate }));
    },
    [dateRange.lte]
  );

  /**
   * Handles end date changes with validation
   * Prevents selection of end dates in the future or earlier than the current start date
   */
  const handleEndDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newEndDate = event.target.value;
      if (!newEndDate) return;

      // First check: Validate that end date is not in the future
      if (newEndDate > MAX_DATE) {
        setDateErrors((prev) => ({
          ...prev,
          end: 'End date cannot be in the future',
        }));
        return; // Prevent the change
      }

      // Second check: Validate that end date is not earlier than start date
      if (newEndDate < dateRange.gte) {
        setDateErrors((prev) => ({
          ...prev,
          end: 'End date cannot be earlier than start date',
        }));
        return; // Prevent the change
      }

      // Clear any existing end date error
      setDateErrors((prev) => ({
        ...prev,
        end: undefined,
      }));

      setDateRange((prev) => ({ ...prev, lte: newEndDate }));
    },
    [dateRange.gte, MAX_DATE]
  );

  return (
    <div className="relative inline-block text-left">
      {/* Export Button */}
      <button
        type="button"
        id={`${table}-export-menu-icon-button`}
        data-testid={`${table}-export-menu-icon-button`}
        ref={buttonRef}
        className={`w-9 h-9 px-3.5 py-3 bg-white rounded-full outline outline-[0.83px] outline-offset-[-0.83px] outline-neutral-300 inline-flex justify-center items-center gap-1.5 ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-neutral-100 cursor-pointer'
        }`}
        onClick={toggleMenu}
        disabled={disabled}
        aria-label="Export company data with date range"
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-controls={`${table}-export-menu`}
        title={
          disabled
            ? 'Export is disabled for datasets with more than 5,000 records'
            : 'Export company data with date range selection'
        }
      >
        {combinedIsExporting ? (
          <Icon
            icon={faSpinner}
            className="w-3 h-3 animate-spin text-neutral-600"
            data-testid={`${table}-export-loading`}
          />
        ) : (
          <div className="w-4 h-4 overflow-hidden flex justify-center items-center">
            <Icon
              icon={faDownload}
              className={`w-4 h-3 ${
                disabled ? 'text-neutral-400' : 'text-neutral-600'
              }`}
            />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          id={`${table}-export-menu`}
          data-testid={`${table}-export-menu`}
          className="absolute right-0 mt-1 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          ref={dropdownRef}
          role="menu"
          aria-label="Export options with date range"
        >
          <div
            className="p-4 max-h-80 overflow-y-auto flex flex-col gap-4"
            role="none"
          >
            {/* Date Range Picker Section */}
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-neutral-700">
                User Activities by Company
              </p>

              <div className="flex flex-col gap-3">
                {/* Start Date Input */}
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-neutral-600">Start Date</p>
                  {dateErrors.start && (
                    <div
                      id={`${table}-start-date-error`}
                      className="text-sm text-red-600"
                      role="alert"
                      aria-live="polite"
                    >
                      {dateErrors.start}
                    </div>
                  )}
                  <input
                    type="date"
                    value={dateRange.gte}
                    onChange={handleStartDateChange}
                    aria-label="Select start date"
                    data-testid={`${table}-start-date-input`}
                    aria-describedby={
                      dateErrors.start ? `${table}-start-date-error` : undefined
                    }
                    aria-invalid={!!dateErrors.start}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>

                {/* End Date Input */}
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-neutral-600">End Date</p>
                  {dateErrors.end && (
                    <div
                      id={`${table}-end-date-error`}
                      className="text-sm text-red-600"
                      role="alert"
                      aria-live="polite"
                    >
                      {dateErrors.end}
                    </div>
                  )}
                  <input
                    type="date"
                    value={dateRange.lte}
                    onChange={handleEndDateChange}
                    aria-label="Select end date"
                    data-testid={`${table}-end-date-input`}
                    aria-describedby={
                      dateErrors.end ? `${table}-end-date-error` : undefined
                    }
                    aria-invalid={!!dateErrors.end}
                    max={MAX_DATE}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="flex flex-col gap-3">
              <div className="border-t border-neutral-200" />
            </div>

            {/* Export Options */}
            <div className="flex flex-col">
              <ExportMenuItem
                type="csv"
                onExport={handleExport}
                id={`${table}-export-menu-item-csv`}
                data-testid={`${table}-export-menu-item-csv`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyTableExportMenu;
